import { useCallback, useEffect, useRef, useState } from 'react';

import {
	type CapabilitiesOutcome,
	findCancellationForUnit,
	getCapabilities,
	type ICancellationRow,
} from '../lib/cancellation-api';
import {
	getRecord,
	type ICancellationRecord,
	type ICancellationSnapshot,
	readServerRow,
	readSnapshot,
	snapshotOf,
	subscribeCancellationChanged,
	writeServerRow,
} from '../lib/cancellation-store';

const POLL_MS = 60_000;
/** While the unit is locked the agent is waiting on one thing — the party's reinstatement. */
const LOCKED_POLL_MS = 20_000;

export interface ICancellationStatus {
	caps: CapabilitiesOutcome | null;
	record: ICancellationRecord | null;
	serverRow: ICancellationRow | null;
	snapshot: ICancellationSnapshot;
	refresh: () => void;
}

/**
 * The cancellation state of the agent's own polling unit: the on-device record (instant, works
 * offline) merged with the newest server row (truth once known — an admin may have confirmed,
 * reinstated or entered it). Polls gently so "Reviewed by party" appears without a reload.
 */
export const useCancellationStatus = (
	electionId: number | undefined,
	puCode: string | undefined,
	enabled: boolean
): ICancellationStatus => {
	const active = enabled && !!electionId && !!puCode;
	const read = useCallback(
		() => ({
			record: active ? getRecord(electionId, puCode) : null,
			serverRow: active ? readServerRow(electionId, puCode) : null,
		}),
		[active, electionId, puCode]
	);
	const [local, setLocal] = useState(read);
	const [caps, setCaps] = useState<CapabilitiesOutcome | null>(null);
	const alive = useRef(true);

	useEffect(() => {
		setLocal(read());
		if (!active) return;
		return subscribeCancellationChanged(electionId, puCode, () => setLocal(read()));
	}, [active, electionId, puCode, read]);

	const refresh = useCallback(async () => {
		if (!active) return;
		const outcome = await getCapabilities(electionId);
		if (!alive.current) return;
		setCaps(outcome);
		if (outcome.mode === 'unknown') return;
		const res = await findCancellationForUnit(electionId, puCode, outcome.mode);
		if (!alive.current || res.kind !== 'ok') return;
		// A v1 miss only means "not in the first page we could see" — never erase on it.
		if (res.data || outcome.mode === 'v2') writeServerRow(electionId, puCode, res.data);
	}, [active, electionId, puCode]);

	const snapshot = snapshotOf(local.record, local.serverRow);
	const locked = snapshot.active && !snapshot.localOnly;

	useEffect(() => {
		alive.current = true;
		if (!active) return;
		void refresh();
		const onOnline = () => void refresh();
		window.addEventListener('online', onOnline);
		return () => {
			alive.current = false;
			window.removeEventListener('online', onOnline);
		};
	}, [active, refresh]);

	useEffect(() => {
		if (!active) return;
		const timer = window.setInterval(
			() => {
				if (document.visibilityState === 'visible') void refresh();
			},
			locked ? LOCKED_POLL_MS : POLL_MS
		);
		return () => window.clearInterval(timer);
	}, [active, locked, refresh]);

	// Re-read the server once the report lands, so review state follows it.
	const sent = local.record?.report.state === 'sent';
	useEffect(() => {
		if (sent) void refresh();
	}, [sent, refresh]);

	return {
		caps,
		record: local.record,
		serverRow: local.serverRow,
		snapshot,
		refresh: () => void refresh(),
	};
};

/**
 * Device-only view of the same state — no requests. For surfaces that sit beside a
 * `useCancellationStatus` owner (the overview) and must not double its polling.
 */
export const useCancellationSnapshot = (
	electionId: number | undefined,
	puCode: string | undefined,
	enabled: boolean
): ICancellationSnapshot => {
	const active = enabled && !!electionId && !!puCode;
	const [snapshot, setSnapshot] = useState<ICancellationSnapshot>(() =>
		active ? readSnapshot(electionId, puCode) : { active: false, localOnly: false }
	);
	useEffect(() => {
		if (!active) {
			setSnapshot({ active: false, localOnly: false });
			return;
		}
		const read = () => setSnapshot(readSnapshot(electionId, puCode));
		read();
		return subscribeCancellationChanged(electionId, puCode, read);
	}, [active, electionId, puCode]);
	return snapshot;
};
